/**
 * Member Analytics Modal Component (Story 3.4)
 *
 * Displays detailed analytics for a merchant member including:
 * - Financial summary (redemptions, spending, credits, points)
 * - Sharing activity (coupons shared, referrals, commission)
 * - Recent activity (last 5 transactions)
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '@/i18n';
import MemberMetricCard from './MemberMetricCard';
import PersonalMemberCardSection from './PersonalMemberCardSection';
import MemberGroupCardsSection from './MemberGroupCardsSection';
import { formatMoney, TimestampService, type ApiTimestamp, type GetMemberResponseType } from '@zlinkx/shared-v2';

/**
 * Member data with analytics for the modal
 * Should be provided by the parent page via useGetMember hook
 */
type MemberData = GetMemberResponseType['data'];

interface MemberAnalyticsModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /** User ID for navigation purposes */
  userId: string | null;
  merchantId?: number;
  /** Member data from parent (via useGetMember hook) */
  member: MemberData | null | undefined;
  /** Whether member data is currently loading */
  isLoading: boolean;
  /** Error from fetching member data */
  error: Error | null;
  /** Callback to refetch member data */
  onRefetch: () => void;
}

const MemberAnalyticsModal: React.FC<MemberAnalyticsModalProps> = ({
  isOpen,
  onClose,
  userId,
  member,
  isLoading,
  error,
  onRefetch,
  merchantId = 0,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Handle view all transactions navigation
  const handleViewAllTransactions = () => {
    if (userId) {
      void navigate(`/merchant/transactions?member=${userId}`);
      onClose();
    }
  };

  if (!isOpen) return null;

  // Format date for display
  const formatDate = (date: Date | ApiTimestamp): string => {
    const parsed = date instanceof Date ? date : TimestampService.parseDateTimeInput(date);
    if (!TimestampService.isValidDate(parsed)) {
      return t('common.noData');
    }

    const formatted = TimestampService.formatForDisplay(parsed, { style: 'short' });
    const [dateOnly] = formatted.split(',');
    return dateOnly ?? formatted;
  };

  const formatNumber = (value: number): string => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  // Format activity type for display
  const formatActivityType = (
    type: 'redemption' | 'credit_usage' | 'prepayment'
  ): string => {
    return t(`merchant.analytics.activityTypes.${type}`);
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40"
        onClick={onClose}
        data-testid="modal-backdrop"
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden"
          data-testid="member-analytics-modal"
        >
          {/* Header */}
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                {isLoading ? (
                  <div className="animate-pulse">
                    <div className="h-6 bg-gray-200 rounded w-48 mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-32"></div>
                  </div>
                ) : error ? (
                  <h2 className="text-xl font-semibold text-red-600">
                    {t('common.error')}
                  </h2>
                ) : member ? (
                  <>
                    <h2 className="text-xl font-semibold text-gray-900">
                      {t('merchant.analytics.modalTitle', {
                        name: member.display_name || t('common.noData'),
                      })}
                    </h2>
                    <p className="text-sm text-gray-600 mt-1">
                      {t('merchant.analytics.memberSince', {
                        date: formatDate(member.member_since),
                      })}
                    </p>
                  </>
                ) : null}
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                aria-label={t('merchant.analytics.close')}
              >
                <svg
                  className="w-5 h-5 text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
            {isLoading ? (
              <div className="space-y-8">
                <div className="animate-pulse">
                  <div className="h-5 bg-gray-200 rounded w-40 mb-4"></div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {Array.from({ length: 4 }).map((_, i) => (
                      <div
                        key={i}
                        className="bg-gray-100 rounded-lg h-24"
                      ></div>
                    ))}
                  </div>
                </div>
              </div>
            ) : error ? (
              <div className="text-center py-8">
                <p className="text-red-600 mb-4">{t('merchant.analytics.loadFailed')}</p>
                <button
                  onClick={onRefetch}
                  className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-colors"
                >
                  {t('merchant.analytics.retry')}
                </button>
              </div>
            ) : member ? (
              <>
                {merchantId > 0 && userId !== null && (
                  <>
                    <PersonalMemberCardSection key={`${merchantId}:${userId}`} merchantId={merchantId} userId={userId} />
                    <MemberGroupCardsSection key={`groups:${merchantId}:${userId}`} merchantId={merchantId} userId={userId} />
                  </>
                )}
                {/* Financial Summary Section */}
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    {t('merchant.analytics.financialSummary')}
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <MemberMetricCard
                      title={t('merchant.analytics.totalRedemptions')}
                      value={member.analytics.financial_summary.total_redemptions}
                      format="number"
                      icon={
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                          />
                        </svg>
                      }
                    />
                    <MemberMetricCard
                      title={t('merchant.analytics.totalSpending')}
                      value={member.analytics.financial_summary.total_spending}
                      format="currency"
                      icon={
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      }
                    />
                    <MemberMetricCard
                      title={t('merchant.analytics.creditBalance')}
                      value={member.analytics.financial_summary.credit_balance}
                      format="currency"
                      valueClassName="text-green-600"
                      icon={
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                          />
                        </svg>
                      }
                    />
                    <MemberMetricCard
                      title={t('merchant.analytics.pointsAvailable')}
                      value={`${formatNumber(member.analytics.financial_summary.points_balance.available)} ${t('merchant.analytics.pts')}`}
                      icon={
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                          />
                        </svg>
                      }
                    />
                  </div>
                </div>

                {/* Sharing Activity Section */}
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    {t('merchant.analytics.sharingActivity')}
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <MemberMetricCard
                      title={t('merchant.analytics.couponsShared')}
                      value={member.analytics.sharing_activity.coupons_shared}
                      format="number"
                      icon={
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m9.632 4.684C18.114 15.938 18 15.482 18 15c0-.482.114-.938.316-1.342m0 2.684a3 3 0 110-2.684M9 9a3 3 0 110-6 3 3 0 010 6zm6 12a3 3 0 110-6 3 3 0 010 6zM9 9l6 12"
                          />
                        </svg>
                      }
                    />
                    <MemberMetricCard
                      title={t('merchant.analytics.successfulReferrals')}
                      value={member.analytics.sharing_activity.successful_referrals}
                      format="number"
                      valueClassName="text-blue-600"
                      icon={
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                          />
                        </svg>
                      }
                    />
                    <MemberMetricCard
                      title={t('merchant.analytics.commissionEarned')}
                      value={`${formatNumber(member.analytics.sharing_activity.commission_earned)} ${t('merchant.analytics.pts')}`}
                      valueClassName="text-amber-600"
                      icon={
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                          />
                        </svg>
                      }
                    />
                  </div>
                </div>

                {/* Recent Activity Section */}
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    {t('merchant.analytics.recentActivity')}
                  </h3>
                  {member.analytics.recent_activity.length > 0 ? (
                    <div className="bg-gray-50 rounded-lg overflow-hidden">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              {t('merchant.analytics.table.type')}
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              {t('merchant.analytics.table.amount')}
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              {t('merchant.analytics.table.date')}
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {member.analytics.recent_activity.map(
                            (activity, index) => (
                              <tr key={index}>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                  {formatActivityType(activity.type)}
                                </td>
                                <td
                                  className={`px-4 py-3 whitespace-nowrap text-sm text-right font-medium ${
                                    activity.amount < 0
                                      ? 'text-red-600'
                                      : 'text-green-600'
                                  }`}
                                >
                                  {activity.amount < 0 ? '-' : '+'}
                                  {formatMoney(Math.abs(activity.amount))}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 text-right">
                                  {formatDate(activity.created_at)}
                                </td>
                              </tr>
                            )
                          )}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="bg-gray-50 rounded-lg p-6 text-center">
                      <p className="text-gray-500">
                        {t('merchant.analytics.noRecentActivity')}
                      </p>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-200">
                  <button
                    onClick={handleViewAllTransactions}
                    className="flex-1 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-colors text-center"
                  >
                    {t('merchant.analytics.viewAllTransactions')}
                  </button>
                  <button
                    disabled
                    title={t('merchant.analytics.adjustCreditComingSoon')}
                    className="flex-1 px-4 py-2 bg-gray-200 text-gray-500 rounded-lg font-medium cursor-not-allowed text-center"
                  >
                    {t('merchant.analytics.adjustCredit')}
                  </button>
                </div>
              </>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
};

export default MemberAnalyticsModal;
